import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { 
  Storefront, 
  ChartBar, 
  Shield, 
  QrCode, 
  Users, 
  MapPin, 
  CheckCircle, 
  ArrowRight,
  Star,
  Sparkle,
  Clock,
  Eye,
  DeviceMobile,
  TrendUp
} from "@phosphor-icons/react";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { useState, useEffect } from "react";

export const Route = createFileRoute("/partners")({
  component: PartnersLandingPage,
});

function PartnersLandingPage() {
  const { t } = useTranslation("partners");
  const currentLang = getCurrentLanguage();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-border' : 'bg-transparent'}`}>
        <div className="container mx-auto max-w-6xl px-3 sm:px-0 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center pt-1 gap-2 cursor-pointer">
            <img 
              src="/suuper_logo.svg" 
              alt="Suuper"
              className="h-5 sm:h-8 w-auto"
            />
            <span className="text-1xl sm:text-2xl text-text">Iași</span>
          </Link>

          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-text-light hover:text-text transition-colors whitespace-nowrap"
            >
              {currentLang === "ro" ? "Înapoi" : "Back"}
            </Link>
            <a
              href="/partner/login"
              className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium bg-primary-gradient text-white rounded-lg hover:shadow-lg hover:shadow-primary/30 transition-all whitespace-nowrap"
            >
              {currentLang === "ro" ? "Portal partener" : "Partner portal"}
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/6 via-background to-background" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] bg-primary/20 blur-3xl rounded-full" />

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="badge badge-primary mb-4">
              <span className="inline-flex items-center gap-2">
                <Storefront weight="fill" className="w-4 h-4 text-primary" />
                {currentLang === "ro" ? "Pentru parteneri" : "For partners"}
              </span>
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-text mb-6 tracking-tight">
              {t("landing.hero.title")}
            </h1>
            <p className="text-lg md:text-xl text-text-light mb-8 max-w-2xl mx-auto">
              {t("landing.hero.subtitle")}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/partner/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary-gradient text-white rounded-xl font-semibold text-lg hover:shadow-xl hover:shadow-primary/30 transition-all shadow-lg shadow-primary/25"
              >
                {t("landing.hero.ctaPrimary")}
                <ArrowRight size={20} />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-text rounded-xl font-semibold text-lg hover:bg-background-dark transition-colors border border-border"
              >
                {t("landing.hero.ctaSecondary")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="badge badge-primary">
              {currentLang === "ro" ? "Problema" : "The Problem"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text mt-4 mb-4">
              {t("landing.problem.title")}
            </h2>
            <p className="text-text-light">
              {t("landing.problem.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <ProblemCard
              icon={<TrendUp size={24} />}
              problem={t("landing.problem.items.onlineOffers")}
              solution={currentLang === "ro" ? "Ofertele se activează doar în locație" : "Offers activate only at location"}
            />
            <ProblemCard
              icon={<Users size={24} />}
              problem={t("landing.problem.items.clientsDontCome")}
              solution={currentLang === "ro" ? "Clienții vin să folosească reducerea" : "Customers come to use the discount"}
            />
            <ProblemCard
              icon={<Shield size={24} />}
              problem={t("landing.problem.items.noAntiFraud")}
              solution={currentLang === "ro" ? "10+ mecanisme anti-fraudă proprietare" : "10+ proprietary anti-fraud mechanisms"}
            />
            <ProblemCard
              icon={<QrCode size={24} />}
              problem={t("landing.problem.items.complicatedProcess")}
              solution={currentLang === "ro" ? "Scanare QR în 3 secunde" : "QR scan in 3 seconds"}
            />
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl border border-border shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <span className="badge badge-primary mb-4">
                {t("landing.founder.title")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-text mb-4">
                {t("landing.founder.name")}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <StatBadge
                number="14+"
                label={currentLang === "ro" ? "Ani experiență" : "Years experience"}
              />
              <StatBadge
                number="30+"
                label={currentLang === "ro" ? "Companii" : "Companies"}
              />
              <StatBadge
                number="4M"
                label={currentLang === "ro" ? "Lei/lună" : "Lei/month"}
              />
            </div>

            <div className="space-y-4 text-center">
              <p className="text-text-light">
                <strong className="text-text">{t("landing.founder.experience")}</strong>
              </p>
              <p className="text-text-light">
                <strong className="text-text">{t("landing.founder.companies")}</strong>
              </p>
              <p className="text-text-light">
                <strong className="text-text">{t("landing.founder.results")}</strong>
              </p>
              <div className="pt-4 border-t border-border">
                <p className="text-lg font-semibold text-primary italic">
                  "{t("landing.founder.quote")}"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Offers Work Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="badge badge-primary">
              {currentLang === "ro" ? "Marketing dovedit" : "Proven Marketing"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text mt-4 mb-4">
              {t("landing.whyOffersWork.title")}
            </h2>
            <p className="text-text-light">
              {t("landing.whyOffersWork.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <WhyOffersWorkCard
              icon={<Star size={24} />}
              title={t("landing.whyOffersWork.items.exclusive")}
            />
            <WhyOffersWorkCard
              icon={<CheckCircle size={24} />}
              title={t("landing.whyOffersWork.items.realDiscounts")}
            />
            <WhyOffersWorkCard
              icon={<Users size={24} />}
              title={t("landing.whyOffersWork.items.activeCommunity")}
            />
            <WhyOffersWorkCard
              icon={<MapPin size={24} />}
              title={t("landing.whyOffersWork.items.locationOnly")}
            />
            <WhyOffersWorkCard
              icon={<Sparkle size={24} />}
              title={t("landing.whyOffersWork.items.inviteSystem")}
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="badge badge-primary">
              {currentLang === "ro" ? "Beneficii" : "Benefits"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text mt-4 mb-4">
              {t("landing.benefits.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <BenefitCard
              icon={<Storefront size={24} />}
              title={t("landing.benefits.items.realTraffic.title")}
              description={t("landing.benefits.items.realTraffic.description")}
            />
            <BenefitCard
              icon={<TrendUp size={24} />}
              title={t("landing.benefits.items.guaranteedOrders.title")}
              description={t("landing.benefits.items.guaranteedOrders.description")}
            />
            <BenefitCard
              icon={<MapPin size={24} />}
              title={t("landing.benefits.items.visibility.title")}
              description={t("landing.benefits.items.visibility.description")}
            />
            <BenefitCard
              icon={<ChartBar size={24} />}
              title={t("landing.benefits.items.detailedStats.title")}
              description={t("landing.benefits.items.detailedStats.description")}
            />
            <BenefitCard
              icon={<Shield size={24} />}
              title={t("landing.benefits.items.zeroFraud.title")}
              description={t("landing.benefits.items.zeroFraud.description")}
            />
            <BenefitCard
              icon={<QrCode size={24} />}
              title={t("landing.benefits.items.simpleProcess.title")}
              description={t("landing.benefits.items.simpleProcess.description")}
            />
            <BenefitCard
              icon={<Eye size={24} />}
              title={t("landing.benefits.items.fullControl.title")}
              description={t("landing.benefits.items.fullControl.description")}
            />
            <BenefitCard
              icon={<CheckCircle size={24} />}
              title={t("landing.benefits.items.transparentCosts.title")}
              description={t("landing.benefits.items.transparentCosts.description")}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="badge badge-primary">
              {currentLang === "ro" ? "Proces simplu" : "Simple Process"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text mt-4 mb-4">
              {t("landing.howItWorks.title")}
            </h2>
            <p className="text-text-light">
              {t("landing.howItWorks.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <StepCard
              step={1}
              icon={<DeviceMobile size={32} />}
              title={t("landing.howItWorks.steps.step1.title")}
              description={t("landing.howItWorks.steps.step1.description")}
            />
            <StepCard
              step={2}
              icon={<QrCode size={32} />}
              title={t("landing.howItWorks.steps.step2.title")}
              description={t("landing.howItWorks.steps.step2.description")}
            />
            <StepCard
              step={3}
              icon={<Clock size={32} />}
              title={t("landing.howItWorks.steps.step3.title")}
              description={t("landing.howItWorks.steps.step3.description")}
            />
            <StepCard
              step={4}
              icon={<CheckCircle size={32} />}
              title={t("landing.howItWorks.steps.step4.title")}
              description={t("landing.howItWorks.steps.step4.description")}
            />
          </div>
        </div>
      </section>

      {/* Anti-Fraud Section */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="badge badge-primary">
              {currentLang === "ro" ? "Securitate" : "Security"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text mt-4 mb-4">
              {t("landing.antiFraud.title")}
            </h2>
            <p className="text-text-light">
              {t("landing.antiFraud.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AntiFraudCard title={t("landing.antiFraud.items.temporaryToken")} />
            <AntiFraudCard title={t("landing.antiFraud.items.locationCheck")} />
            <AntiFraudCard title={t("landing.antiFraud.items.deviceFingerprinting")} />
            <AntiFraudCard title={t("landing.antiFraud.items.dailyLimit")} />
            <AntiFraudCard title={t("landing.antiFraud.items.categoryCooldown")} />
            <AntiFraudCard title={t("landing.antiFraud.items.geoAnomalies")} />
            <AntiFraudCard title={t("landing.antiFraud.items.rapidRedemptions")} />
            <AntiFraudCard title={t("landing.antiFraud.items.patternAnomalies")} />
            <AntiFraudCard title={t("landing.antiFraud.items.autoBan")} />
            <AntiFraudCard title={t("landing.antiFraud.items.blacklist")} />
            <AntiFraudCard title={t("landing.antiFraud.items.membershipCheck")} />
            <AntiFraudCard title={t("landing.antiFraud.items.uniqueValidation")} />
          </div>
        </div>
      </section>

      {/* Registration Process Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="badge badge-primary">
              {currentLang === "ro" ? "Înregistrare" : "Registration"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text mt-4 mb-4">
              {t("landing.registration.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <StepCard
              step={1}
              title={t("landing.registration.steps.step1.title")}
              description={t("landing.registration.steps.step1.description")}
            />
            <StepCard
              step={2}
              title={t("landing.registration.steps.step2.title")}
              description={t("landing.registration.steps.step2.description")}
            />
            <StepCard
              step={3}
              title={t("landing.registration.steps.step3.title")}
              description={t("landing.registration.steps.step3.description")}
            />
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <span className="badge badge-primary">
              {currentLang === "ro" ? "Dashboard" : "Dashboard"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-text mt-4 mb-4">
              {t("landing.dashboard.title")}
            </h2>
            <p className="text-text-light">
              {t("landing.dashboard.subtitle")}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-xl p-8">
            <div className="grid md:grid-cols-3 gap-6">
              <DashboardFeature
                icon={<ChartBar size={24} />}
                title={t("landing.dashboard.features.todayRedemptions")}
              />
              <DashboardFeature
                icon={<TrendUp size={24} />}
                title={t("landing.dashboard.features.monthRedemptions")}
              />
              <DashboardFeature
                icon={<Users size={24} />}
                title={t("landing.dashboard.features.uniqueUsers")}
              />
              <DashboardFeature
                icon={<ChartBar size={24} />}
                title={t("landing.dashboard.features.performanceCharts")}
              />
              <DashboardFeature
                icon={<Clock size={24} />}
                title={t("landing.dashboard.features.redemptionHistory")}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-24 px-4 bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-pattern-lines opacity-20" />
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] bg-white/10 blur-3xl rounded-full" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] bg-white/10 blur-3xl rounded-full" />
        <div className="container mx-auto max-w-3xl text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t("landing.cta.title")}
          </h2>
          <p className="text-lg text-white/80 mb-8">
            {t("landing.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/partner/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary rounded-xl font-semibold text-lg hover:bg-background transition-colors"
            >
              {t("landing.cta.button")}
              <ArrowRight size={20} />
            </a>
            <div className="text-white/80 text-sm">
              <p>{t("landing.cta.contact")}: {t("landing.registration.contactEmail")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-text">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <MapPin size={20} className="text-white" />
              </div>
              <span className="text-lg font-bold text-white">Suuper</span>
            </div>
            <p className="text-white/40 text-sm text-center sm:text-left">
              © {new Date().getFullYear()} Suuper
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Componente helper
function ProblemCard({ icon, problem, solution }: { icon: React.ReactNode; problem: string; solution: string }) {
  return (
    <div className="p-6 rounded-xl bg-white border border-border shadow-sm">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-red-600 flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-text-light mb-2">{problem}</p>
          <div className="flex items-center gap-2">
            <ArrowRight size={16} className="text-primary" />
            <p className="text-text font-semibold">{solution}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBadge({ number, label }: { number: string; label: string }) {
  return (
    <div className="text-center p-4 rounded-xl bg-primary/10">
      <div className="text-3xl font-bold text-primary mb-1">{number}</div>
      <div className="text-sm text-text-light">{label}</div>
    </div>
  );
}

function WhyOffersWorkCard({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="p-6 rounded-xl bg-white border border-border shadow-sm">
      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-4">
        {icon}
      </div>
      <p className="text-text font-medium">{title}</p>
    </div>
  );
}

function BenefitCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex flex-col gap-4 p-6 rounded-xl bg-white border border-border shadow-sm hover:shadow-lg transition-shadow text-left">
      <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-semibold text-text mb-2">{title}</h3>
        <p className="text-text-light text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function StepCard({ step, icon, title, description }: { step: number; icon?: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center text-center p-6 rounded-xl bg-white/90 border border-border shadow-sm hover:shadow-lg transition-shadow">
      <div className="relative mb-4">
        {icon ? (
          <div className="w-16 h-16 bg-primary-gradient rounded-full flex items-center justify-center text-white mb-2 shadow-primary">
            {icon}
          </div>
        ) : (
          <div className="w-16 h-16 bg-primary-gradient rounded-full flex items-center justify-center text-white text-xl font-bold mb-2 shadow-primary">
            {step}
          </div>
        )}
      </div>
      <h3 className="text-lg font-semibold text-text mb-2">{title}</h3>
      <p className="text-text-light text-sm">{description}</p>
    </div>
  );
}

function AntiFraudCard({ title }: { title: string }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-white border border-border shadow-sm">
      <Shield size={20} className="text-success flex-shrink-0 mt-0.5" weight="fill" />
      <p className="text-sm text-text">{title}</p>
    </div>
  );
}

function DashboardFeature({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl bg-background border border-border">
      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
        {icon}
      </div>
      <p className="text-sm font-medium text-text">{title}</p>
    </div>
  );
}
